import React, { useState, useEffect, useRef } from "react";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import api from "../Utils/api";
import { format, startOfWeek, endOfWeek, parseISO } from "date-fns";
import Spinner from "../Components/Spinner";
import EditTimesheetModal from "../Components/WorkTimeSheet/EditTimesheetModal";
import DateRangePicker from "../Components/Timesheet/DateRangePicker";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { formatIrelandTime, fromIrelandTime, toIrelandTime } from "../Utils/timezoneHelper";
import Select from "react-select";

export default function WorkTimeSheet() {
  const user = useSelector((state) => state.user.user);
  const [isLoading, setIsLoading] = useState(false);
  const [timesheets, setTimesheets] = useState([]);
  const [users, setUsers] = useState([]);
  const [allUsers, setAllUsers] = useState([]); // Store all users for filtering
  const [locations, setLocations] = useState([]);
  const [filteredTimesheets, setFilteredTimesheets] = useState([]);
  
  // Filters
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [startDate, setStartDate] = useState(() => {
    const today = new Date();
    return startOfWeek(today, { weekStartsOn: 1 });
  });
  const [endDate, setEndDate] = useState(() => {
    const today = new Date();
    return endOfWeek(today, { weekStartsOn: 1 });
  });

  // Staff selection dropdown
  const [isStaffDropdownOpen, setIsStaffDropdownOpen] = useState(false);
  const [staffSearchTerm, setStaffSearchTerm] = useState("");
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const dropdownRef = useRef(null);

  // Edit modal
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedTimesheet, setSelectedTimesheet] = useState(null);

  // Export confirmation modal
  const [showExportModal, setShowExportModal] = useState(false);
  const [selectedExportType, setSelectedExportType] = useState("csv");

  useEffect(() => {
    if (user) {
      getUsers();
      getLocations();
      if (user.deputyAccessLevel === "system_administrator") {
        fetchCompanies();
      }
    }
  }, [user]);

  const fetchCompanies = async () => {
    try {
      const response = await api.get("/company");
      if (response.data.success) {
        // Only show active (non-archived) companies
        setCompanies((response.data.companies || []).filter(company => !company.archive));
      }
    } catch (error) {
      console.error("Failed to fetch companies:", error);
    }
  };

  useEffect(() => {
    if (startDate && endDate) {
      getTimesheets();
    }
  }, [startDate, endDate]);

  useEffect(() => {
    filterTimesheets();
  }, [timesheets, selectedUsers]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsStaffDropdownOpen(false);
      }
    };

    if (isStaffDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isStaffDropdownOpen]);

  const getUsers = async () => {
    try {
      setIsLoading(true);
      const response = await api.get("/user");
      setIsLoading(false);
      const employeeUsers = response.data.users.filter(
        (u) => !u.archive && (u.deputyAccessLevel === "employee" || u.deputyAccessLevel === "company_manager")
      );
      setAllUsers(employeeUsers); // Store all users
      // Apply company filter if selected
      let filteredUsers = employeeUsers;
      if (user && user.deputyAccessLevel === "system_administrator" && selectedCompany) {
        filteredUsers = employeeUsers.filter((u) => {
          // Handle both populated and non-populated companyID
          const userCompanyID = u.companyID?._id || u.companyID;
          return userCompanyID && userCompanyID.toString() === selectedCompany._id.toString();
        });
      }
      setUsers(filteredUsers);
      setSelectedUsers(filteredUsers.map((u) => u._id)); // Select all by default
    } catch (error) {
      console.error(error);
      toast.error("Failed to fetch users");
    }
  };

  // Re-filter users when company selection changes
  useEffect(() => {
    if (user && user.deputyAccessLevel === "system_administrator" && allUsers.length > 0) {
      let filteredUsers = allUsers;
      if (selectedCompany) {
        filteredUsers = allUsers.filter((u) => {
          // Handle both populated and non-populated companyID
          const userCompanyID = u.companyID?._id || u.companyID;
          return userCompanyID && userCompanyID.toString() === selectedCompany._id.toString();
        });
      }
      setUsers(filteredUsers);
      setSelectedUsers(filteredUsers.map((u) => u._id)); // Select all filtered users
    }
  }, [selectedCompany, allUsers, user]);

  const getLocations = async () => {
    try {
      const response = await api.get("/location");
      setLocations(response.data.locations.filter((loc) => !loc.archive));
    } catch (error) {
      console.error(error);
      toast.error("Failed to fetch locations");
    }
  };

  const getTimesheets = async () => {
    try {
      setIsLoading(true);
      const start = new Date(startDate);
      start.setUTCHours(0, 0, 0, 0);
      const end = new Date(endDate);
      end.setUTCHours(23, 59, 59, 999);

      const response = await api.post("/timesheet/list", {
        startTime: start.toISOString(),
        endTime: end.toISOString(),
      });
      
      setTimesheets(response.data.timesheets || []);
      setIsLoading(false);
    } catch (error) {
      console.error(error);
      toast.error("Failed to fetch timesheets");
      setIsLoading(false);
    }
  };

  const handleDateChange = (newStartDate, newEndDate) => {
    setStartDate(newStartDate);
    setEndDate(newEndDate);
  };

  const filterTimesheets = () => {
    const filtered = timesheets.filter((timesheet) =>
      selectedUsers.includes(timesheet.userID?._id)
    );
    setFilteredTimesheets(filtered);
  };

  const formatTime = (dateString) => {
    return formatIrelandTime(dateString, 'time');
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const irelandDate = toIrelandTime(dateString);
    return format(irelandDate, "dd.MM.yyyy");
  };

  const formatHours = (hours) => {
    if (!hours && hours !== 0) return "-";
    // Convert hours to total seconds for precise calculation
    const totalSeconds = Math.round(hours * 3600);
    const hoursPart = Math.floor(totalSeconds / 3600);
    const minutesPart = Math.floor((totalSeconds % 3600) / 60);
    const secondsPart = totalSeconds % 60;
    return `${String(hoursPart).padStart(2, "0")}:${String(minutesPart).padStart(2, "0")}:${String(secondsPart).padStart(2, "0")}`;
  };

  const getUserFullName = (user) => {
    const firstName = user.firstName || "";
    const lastName = user.lastName || "";
    return `${firstName} ${lastName}`.trim() || user.preferredName || "Unknown";
  };

  const getLocationName = (locationID) => {
    // If locationID is already a populated object with name property
    if (locationID && typeof locationID === 'object' && locationID.name) {
      return locationID.name;
    }
    
    // Otherwise, find it in the locations array
    const location = locations.find((l) => l._id === locationID);
    return location ? location.name : "UNKNOWN LOCATION";
  };

  const toggleUserSelection = (userID) => {
    setSelectedUsers((prev) =>
      prev.includes(userID)
        ? prev.filter((id) => id !== userID)
        : [...prev, userID]
    );
  };

  const toggleAllUsers = () => {
    if (selectedUsers.length === users.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(users.map((u) => u._id));
    }
  };

  const getSelectedUsersNames = () => {
    if (selectedUsers.length === 0) return "Select staff";
    if (selectedUsers.length === users.length) return "All staff";
    const selected = users.filter((u) => selectedUsers.includes(u._id));
    if (selected.length === 1) return getUserFullName(selected[0]);
    return `${selected.length} staff selected`;
  };

  const getFilteredUsers = () => {
    if (!staffSearchTerm) return users;
    return users.filter((user) => {
      const fullName = getUserFullName(user);
      return fullName.toLowerCase().includes(staffSearchTerm.toLowerCase());
    });
  };

  const calculateTotals = () => {
    let totalWorkTime = 0;
    let totalOvertime = 0;
    
    filteredTimesheets.forEach((ts) => {
      totalWorkTime += ts.totalWorkTime || 0;
      totalOvertime += ts.overtime || 0;
    });

    return {
      totalWorkTime: totalWorkTime, // Keep full precision, don't round
      totalOvertime: totalOvertime, // Keep full precision, don't round
    };
  };

  const handleEdit = (timesheet) => {
    setSelectedTimesheet(timesheet);
    setShowEditModal(true);
  };

  const handleSaveEdit = async (editedData) => {
    try {
      setIsLoading(true);
      const response = await api.post("/timesheet/update", {
        timesheetID: selectedTimesheet._id,
        updatedData: editedData,
      });

      if (response.data.success) {
        toast.success("Timesheet updated successfully");
        getTimesheets();
        setShowEditModal(false);
        setSelectedTimesheet(null);
      } else {
        toast.error(response.data.message || "Failed to update timesheet");
      }
    } catch (error) {
      console.error(error);
      toast.error("Failed to update timesheet");
    } finally {
      setIsLoading(false);
    }
  };

  const handleExport = () => {
    setShowExportModal(true);
  };

  const confirmExport = () => {
    switch (selectedExportType) {
      case "csv":
        performCSVExport();
        break;
      case "xlsx":
        performXLSXExport();
        break;
      case "pdf":
        performPDFExport();
        break;
      default:
        performCSVExport();
    }
    setShowExportModal(false);
  };

  const performCSVExport = () => {
    const { totalWorkTime, totalOvertime } = calculateTotals();
    
    const headers = [
      "Date",
      "Name",
      "Clock In Location",
      "Clock In",
      "Clock Out Location",
      "Clock Out",
      "Clock Time (h)",
      "Break Time (h)",
      "Total Work Time (h)",
      "Total Overtime (h)",
      "Notes",
    ];

    const csvRows = [
      headers.join(","),
      ...filteredTimesheets.map((ts) => {
        const values = [
          formatDate(ts.startTime),
          getUserFullName(ts.userID),
          getLocationName(ts.clockInLocationID),
          formatTime(ts.startTime),
          getLocationName(ts.clockOutLocationID),
          formatTime(ts.endTime),
          formatHours(ts.clockTime),
          formatHours((ts.breakDuration || 0) / 60),
          formatHours(ts.totalWorkTime),
          formatHours(ts.overtime),
          ts.notes || "",
        ];
        return values.map((v) => `"${v}"`).join(",");
      }),
      "",
      `Total Work Time for selection:,${totalWorkTime}`,
      `Total Overtime for selection:,${totalOvertime}`,
    ];

    const csv = csvRows.join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    const filename = `worktimesheet-${formatDate(startDate).replace(/\./g, '-')}-${formatDate(endDate).replace(/\./g, '-')}.csv`;
    a.download = filename;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const performXLSXExport = () => {
    const { totalWorkTime, totalOvertime } = calculateTotals();
    
    const headers = [
      "Date",
      "Name",
      "Clock In Location",
      "Clock In",
      "Clock Out Location",
      "Clock Out",
      "Clock Time (h)",
      "Break Time (h)",
      "Total Work Time (h)",
      "Total Overtime (h)",
      "Notes",
    ];

    const data = filteredTimesheets.map((ts) => [
      formatDate(ts.startTime),
      getUserFullName(ts.userID),
      getLocationName(ts.clockInLocationID),
      formatTime(ts.startTime),
      getLocationName(ts.clockOutLocationID),
      formatTime(ts.endTime),
      formatHours(ts.clockTime),
      formatHours((ts.breakDuration || 0) / 60),
      formatHours(ts.totalWorkTime),
      formatHours(ts.overtime),
      ts.notes || "",
    ]);

    // Add summary rows
    data.push([]);
    data.push(["Total Work Time for selection", "", "", "", "", "", "", "", formatHours(parseFloat(totalWorkTime)), formatHours(parseFloat(totalOvertime)), ""]);

    const ws = XLSX.utils.aoa_to_sheet([headers, ...data]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Timesheets");

    const filename = `worktimesheet-${formatDate(startDate).replace(/\./g, '-')}-${formatDate(endDate).replace(/\./g, '-')}.xlsx`;
    XLSX.writeFile(wb, filename);
  };

  const performPDFExport = () => {
    const { totalWorkTime, totalOvertime } = calculateTotals();
    
    const doc = new jsPDF();

    // Title
    doc.setFontSize(16);
    doc.text("WorkTime Sheet", 14, 20);

    // Date range
    doc.setFontSize(10);
    doc.text(`${formatDate(startDate)} - ${formatDate(endDate)}`, 14, 30);

    // Prepare data
    const headers = [
      ["Date", "Name", "Clock In", "Clock Out", "Clock Time", "Break Time", "Work Time", "Overtime"]
    ];

    const rows = filteredTimesheets.map((ts) => [
      formatDate(ts.startTime),
      getUserFullName(ts.userID),
      formatTime(ts.startTime),
      formatTime(ts.endTime),
      formatHours(ts.clockTime),
      formatHours((ts.breakDuration || 0) / 60),
      formatHours(ts.totalWorkTime),
      formatHours(ts.overtime),
    ]);

    // Add summary
    rows.push([]);
    rows.push(["Total", "", "", "", "", "", formatHours(parseFloat(totalWorkTime)), formatHours(parseFloat(totalOvertime))]);

    autoTable(doc, {
      head: headers,
      body: rows,
      startY: 40,
      styles: { fontSize: 8 },
      headStyles: { fillColor: [158, 158, 158] },
    });

    const filename = `worktimesheet-${formatDate(startDate).replace(/\./g, '-')}-${formatDate(endDate).replace(/\./g, '-')}.pdf`;
    doc.save(filename);
  };

  return (
    <div className="px-6 mt-20 mx-10 pb-20">
      {isLoading && <Spinner />}

      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-gray-600">TimeSheet</h1>
        <button
          onClick={handleExport}
          className="bg-gray-500 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-gray-600 transition"
        >
          Export
        </button>
      </div>

      {/* Filters */}
      <div className="flex gap-4 mb-4 items-center flex-wrap">
        <DateRangePicker
          startDate={startDate}
          endDate={endDate}
          onDateChange={handleDateChange}
        />
        {/* Company Filter - Only for system administrators */}
        {user && user.deputyAccessLevel === "system_administrator" && (
          <div className="flex gap-2 items-center">
            <label className="text-sm font-medium text-gray-700">Company:</label>
            <div className="w-64">
              <Select
                name="company"
                placeholder="All Companies"
                className="basic-select w-full cursor-pointer"
                classNamePrefix="select"
                options={companies}
                value={selectedCompany}
                onChange={(option) => {
                  setSelectedCompany(option);
                }}
                getOptionLabel={(option) => option.name}
                getOptionValue={(option) => option._id}
                isClearable={true}
                styles={{
                  control: (provided) => ({
                    ...provided,
                    borderColor: "#d1d5db",
                    boxShadow: "none",
                    "&:hover": {
                      borderColor: "#9ca3af",
                    },
                  }),
                }}
              />
            </div>
          </div>
        )}
        <div className="flex gap-2 items-center">
          <label className="text-sm font-medium text-gray-700">Staff:</label>
          <div className="relative" ref={dropdownRef}>
            <button
              onClick={() => {
                setIsStaffDropdownOpen(!isStaffDropdownOpen);
                setStaffSearchTerm("");
              }}
              className="w-64 px-3 py-2 bg-white border border-gray-300 rounded-md text-left flex items-center justify-between hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500"
            >
              <span className="text-sm text-gray-700 truncate">
                {getSelectedUsersNames()}
              </span>
              <i className="fa fa-chevron-down text-gray-400 text-xs ml-2"></i>
            </button>

            {isStaffDropdownOpen && (
              <div className="absolute mt-1 w-64 bg-white border border-gray-300 rounded-md shadow-lg z-50 max-h-80 overflow-hidden flex flex-col">
                <div className="p-2 border-b border-gray-200 sticky top-0 bg-white">
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="Search"
                      value={staffSearchTerm}
                      onChange={(e) => setStaffSearchTerm(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-gray-500"
                      autoFocus
                    />
                    <i className="fa fa-search absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm"></i>
                  </div>
                </div>

                <div className="overflow-y-auto max-h-64">
                  <div
                    onClick={toggleAllUsers}
                    className="px-3 py-2 hover:bg-gray-50 cursor-pointer border-b border-gray-200"
                  >
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 border border-gray-300 rounded flex items-center justify-center">
                        {selectedUsers.length === users.length && (
                          <i className="fa fa-check text-xs text-gray-600"></i>
                        )}
                      </div>
                      <span className="text-sm text-gray-700 font-medium">
                        {selectedUsers.length === users.length ? "Deselect All" : "Select All"}
                      </span>
                    </div>
                  </div>

                  {getFilteredUsers().map((user) => (
                    <div
                      key={user._id}
                      onClick={() => toggleUserSelection(user._id)}
                      className="px-3 py-2 hover:bg-gray-50 cursor-pointer"
                    >
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 border border-gray-300 rounded flex items-center justify-center">
                          {selectedUsers.includes(user._id) && (
                            <i className="fa fa-check text-xs text-gray-600"></i>
                          )}
                        </div>
                        <span className="text-sm text-gray-700">{getUserFullName(user)}</span>
                      </div>
                    </div>
                  ))}
                  
                  {getFilteredUsers().length === 0 && (
                    <div className="px-3 py-4 text-center text-sm text-gray-500">
                      No staff found
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto overflow-y-auto rounded-md" style={{ maxHeight: 'calc(100vh - 300px)' }}>
        <table className="w-full border-collapse !rounded-md">
          <thead className="border border-gray-200 !rounded-md">
            <tr className="bg-gray-200 sticky top-0">
              <th className="p-4 text-left font-bold text-gray-700">Date</th>
              <th className="p-4 text-center font-bold text-gray-700">Name</th>
              <th className="p-4 text-center font-bold text-gray-700">Clock In Location</th>
              <th className="p-4 text-center font-bold text-gray-700">Clock In</th>
              <th className="p-4 text-center font-bold text-gray-700">Clock Out Location</th>
              <th className="p-4 text-center font-bold text-gray-700">Clock Out</th>
              <th className="p-4 text-center font-bold text-gray-700">Clock Time (h)</th>
              <th className="p-4 text-center font-bold text-gray-700">Break Time (h)</th>
              <th className="p-4 text-center font-bold text-gray-700">Total Work Time (h)</th>
              <th className="p-4 text-center font-bold text-gray-700">Total Overtime (h)</th>
              <th className="p-4 text-center font-bold text-gray-700">Notes</th>
              <th className="p-4 text-center font-bold text-gray-700">Action</th>
            </tr>
          </thead>
          <tbody>
            {filteredTimesheets.length === 0 ? (
              <tr>
                <td colSpan={12} className="p-4 text-center text-gray-500">
                  No timesheets found
                </td>
              </tr>
            ) : (
              filteredTimesheets.map((timesheet) => (
                <tr key={timesheet._id} className="border-b border-gray-200 hover:bg-gray-50">
                  <td className="p-4 border border-gray-200 text-center text-gray-700">{formatDate(timesheet.startTime)}</td>
                  <td className="p-4 font-medium border border-gray-200 text-center text-gray-700">{getUserFullName(timesheet.userID)}</td>
                  <td className={`p-4 border border-gray-200 text-center ${getLocationName(timesheet.clockInLocationID) === "UNKNOWN LOCATION" ? "text-red-600 font-semibold" : "text-gray-700"}`}>
                    {getLocationName(timesheet.clockInLocationID)}
                  </td>
                  <td className={`p-4 border border-gray-200 text-center ${timesheet.editedFields?.startTime ? "text-red-600 font-semibold" : "text-gray-700"}`}>
                    {formatTime(timesheet.startTime)}
                  </td>
                  <td className={`p-4 border border-gray-200 text-center ${getLocationName(timesheet.clockOutLocationID) === "UNKNOWN LOCATION" ? "text-red-600 font-semibold" : "text-gray-700"}`}>
                    {getLocationName(timesheet.clockOutLocationID)}
                  </td>
                  <td className={`p-4 border border-gray-200 text-center ${timesheet.editedFields?.endTime ? "text-red-600 font-semibold" : "text-gray-700"}`}>
                    {formatTime(timesheet.endTime)}
                  </td>
                  <td className="p-4 border border-gray-200 text-center text-gray-700">{formatHours(timesheet.clockTime)}</td>
                  <td className={`p-4 border border-gray-200 text-center text-gray-700 ${timesheet.editedFields?.breakDuration ? "text-red-600 font-semibold" : ""}`}>
                    {formatHours((timesheet.breakDuration || 0) / 60)}
                  </td>
                  <td className="p-4 border border-gray-200 text-center text-gray-700">{formatHours(timesheet.totalWorkTime)}</td>
                  <td className="p-4 border border-gray-200 text-center text-gray-700">{formatHours(timesheet.overtime)}</td>
                  <td className={`p-4 border border-gray-200 text-center text-gray-700 ${timesheet.editedFields?.notes ? "text-red-600 font-semibold" : ""}`}>
                    {timesheet.notes || ""}
                  </td>
                  <td className="p-4 border border-gray-200 text-center text-gray-700">
                    <button
                      onClick={() => handleEdit(timesheet)}
                      className="w-full bg-gray-300 text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-200 rounded-md"
                    >
                      <i className="w-4 fa fa-edit mr-2 text-gray-500"></i>
                      EDIT
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Summary Section */}
      {filteredTimesheets.length > 0 && (() => {
        const { totalWorkTime, totalOvertime } = calculateTotals();
        return (
          <div className="mt-4 flex justify-end gap-6 bg-white p-4 rounded-lg border border-gray-300 shadow-sm">
            <div className="flex items-center gap-2">
              <span className="text-gray-600">Total Work Time for selection:</span>
              <span className="text-md text-gray-700 font-bold">{formatHours(parseFloat(totalWorkTime))}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-gray-600">Total Overtime for selection:</span>
              <span className="text-md text-gray-700 font-bold">{formatHours(parseFloat(totalOvertime))}</span>
            </div>
          </div>
        );
      })()}

      {/* Edit Modal */}
      {showEditModal && selectedTimesheet && (
        <EditTimesheetModal
          timesheet={selectedTimesheet}
          user={selectedTimesheet.userID}
          locations={locations}
          onClose={() => {
            setShowEditModal(false);
            setSelectedTimesheet(null);
          }}
          onSave={handleSaveEdit}
        />
      )}

      {/* Export Confirmation Modal */}
      {showExportModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-700">Export Format</h3>
              <button
                onClick={() => setShowExportModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <i className="fa fa-times"></i>
              </button>
            </div>
            <p className="text-gray-600 mb-4">Select export format:</p>
            
            {/* Format Selection */}
            <div className="mb-6 space-y-2">
              <button
                onClick={() => setSelectedExportType("csv")}
                className={`w-full px-4 py-3 rounded-md border-2 transition ${
                  selectedExportType === "csv"
                    ? "border-blue-500 bg-blue-50"
                    : "border-gray-300 hover:border-gray-400"
                }`}
              >
                <div className="flex items-center justify-between">
                  <span className="font-medium">CSV</span>
                  <i className={`fa ${selectedExportType === "csv" ? "fa-check-circle text-blue-500" : "fa-circle text-gray-400"}`}></i>
                </div>
              </button>
              <button
                onClick={() => setSelectedExportType("xlsx")}
                className={`w-full px-4 py-3 rounded-md border-2 transition ${
                  selectedExportType === "xlsx"
                    ? "border-blue-500 bg-blue-50"
                    : "border-gray-300 hover:border-gray-400"
                }`}
              >
                <div className="flex items-center justify-between">
                  <span className="font-medium">Excel (XLSX)</span>
                  <i className={`fa ${selectedExportType === "xlsx" ? "fa-check-circle text-blue-500" : "fa-circle text-gray-400"}`}></i>
                </div>
              </button>
              <button
                onClick={() => setSelectedExportType("pdf")}
                className={`w-full px-4 py-3 rounded-md border-2 transition ${
                  selectedExportType === "pdf"
                    ? "border-blue-500 bg-blue-50"
                    : "border-gray-300 hover:border-gray-400"
                }`}
              >
                <div className="flex items-center justify-between">
                  <span className="font-medium">PDF</span>
                  <i className={`fa ${selectedExportType === "pdf" ? "fa-check-circle text-blue-500" : "fa-circle text-gray-400"}`}></i>
                </div>
              </button>
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowExportModal(false)}
                className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 transition"
              >
                Cancel
              </button>
              <button
                onClick={confirmExport}
                className="px-4 py-2 text-white bg-gray-500 rounded-md hover:bg-gray-600 transition"
              >
                Export {selectedExportType.toUpperCase()}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

